import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Edit2, Building2, Wrench, Check, X, AlertCircle, Loader2 } from 'lucide-react';
import GlassPanel from './GlassPanel';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * QuoteSections Component - Phase 1A: Section-Based Quote Engine
 * 
 * Displays and manages quote sections with trade-based pricing.
 * All calculations are NET (no VAT). VAT applied at quote level only.
 */
export function QuoteSections({ quoteId, quoteStatus, onSectionsUpdate }) {
  const { token, currentOrg } = useAuth();
  const [sections, setSections] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  
  const isImmutable = quoteStatus === 'ACCEPTED';

  const fetchSections = useCallback(async () => {
    if (!quoteId || !token) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/quotes/${quoteId}/sections`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to load sections');
      
      const data = await response.json();
      setSections(data.sections || []);
      setSummary(data);
      setError(null);
      
      if (onSectionsUpdate) {
        onSectionsUpdate(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [quoteId, token, onSectionsUpdate]);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  const handleAddSection = async (sectionData) => {
    try {
      const response = await fetch(`${API_URL}/api/quotes/${quoteId}/sections`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sectionData)
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to add section');
      }
      
      setShowAddForm(false);
      fetchSections();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateSection = async (sectionId, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/quotes/${quoteId}/sections/${sectionId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to update section');
      }
      
      setEditingSection(null);
      fetchSections();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Delete this section?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/quotes/${quoteId}/sections/${sectionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to delete section');
      }
      
      fetchSections();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <GlassPanel title="Quote Sections" titleIcon={Wrench}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-xl)', color: 'var(--text-muted)' }}>
          <Loader2 size={20} className="spin" style={{ marginRight: '8px' }} />
          Loading sections...
        </div>
      </GlassPanel>
    );
  }

  return (
    <GlassPanel 
      title="Quote Sections" 
      titleIcon={Wrench}
      headerRight={
        !isImmutable && (
          <button 
            className="btn btn-sm btn-primary"
            onClick={() => setShowAddForm(true)}
            data-testid="add-section-btn"
          >
            <Plus size={14} /> Add Section
          </button>
        )
      }
    >
      {error && (
        <div style={{ 
          padding: 'var(--space-md)', 
          background: 'rgba(239, 68, 68, 0.1)', 
          borderRadius: 'var(--radius-md)',
          color: 'var(--status-error)',
          marginBottom: 'var(--space-md)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Add Section Form */}
      {showAddForm && (
        <SectionForm 
          onSubmit={handleAddSection}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      {/* Sections List */}
      {sections.length === 0 ? (
        <div style={{ 
          padding: 'var(--space-xl)', 
          textAlign: 'center', 
          color: 'var(--text-muted)' 
        }}>
          <Wrench size={32} style={{ marginBottom: 'var(--space-md)', opacity: 0.5 }} />
          <p>No sections yet. Add sections to build your quote pricing.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
          {sections.map(section => (
            editingSection === section.id ? (
              <SectionForm 
                key={section.id}
                initialData={section}
                onSubmit={(data) => handleUpdateSection(section.id, data)}
                onCancel={() => setEditingSection(null)}
              />
            ) : (
              <SectionCard 
                key={section.id}
                section={section}
                isImmutable={isImmutable}
                onEdit={() => setEditingSection(section.id)}
                onDelete={() => handleDeleteSection(section.id)}
              />
            )
          ))}
        </div>
      )}

      {/* Summary */}
      {summary && sections.length > 0 && (
        <div style={{ 
          marginTop: 'var(--space-lg)', 
          paddingTop: 'var(--space-lg)', 
          borderTop: '1px solid var(--glass-border)' 
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-md)' }}>
            <SummaryItem label="Total Cost" value={`£${summary.total_cost?.toFixed(2) || '0.00'}`} />
            <SummaryItem label="Total Revenue" value={`£${summary.total_revenue?.toFixed(2) || '0.00'}`} highlight />
            <SummaryItem label="Total Profit" value={`£${summary.total_profit?.toFixed(2) || '0.00'}`} success={summary.total_profit > 0} />
            <SummaryItem label="Overall Margin" value={`${summary.overall_margin_percentage?.toFixed(1) || '0'}%`} />
          </div>
          
          {!summary.can_accept && (
            <div style={{ 
              marginTop: 'var(--space-md)',
              padding: 'var(--space-sm) var(--space-md)',
              background: 'rgba(245, 158, 11, 0.1)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--status-warning)',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <AlertCircle size={14} />
              {summary.has_missing_margins 
                ? 'Some sections are missing margin. Add margin to all sections before accepting.'
                : 'Add at least one section to enable quote acceptance.'}
            </div>
          )}
        </div>
      )}
    </GlassPanel>
  );
}

function SectionCard({ section, isImmutable, onEdit, onDelete }) {
  const isLegacy = section.is_legacy_section;
  
  return (
    <div 
      style={{
        padding: 'var(--space-md)',
        background: isLegacy ? 'rgba(245, 158, 11, 0.05)' : 'var(--glass-bg-subtle)',
        borderRadius: 'var(--radius-md)',
        border: `1px solid ${isLegacy ? 'rgba(245, 158, 11, 0.2)' : 'var(--glass-border)'}`
      }}
      data-testid={`section-card-${section.id}`}
    >
      {/* Header: Trade Type + Self-performed/Subcontract indicator */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {section.is_subcontract ? (
            <Building2 size={16} style={{ color: 'var(--text-muted)' }} />
          ) : (
            <Wrench size={16} style={{ color: 'var(--text-muted)' }} />
          )}
          <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9375rem' }}>
            {section.trade_type}
          </span>
          {isLegacy && (
            <span className="status-badge pending" style={{ fontSize: '0.625rem', padding: '2px 6px' }}>
              Legacy
            </span>
          )}
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span 
            style={{ 
              fontSize: '0.75rem', 
              color: 'var(--text-muted)',
              padding: '3px 8px',
              background: 'var(--bg-tertiary)',
              borderRadius: '4px'
            }}
          >
            {section.is_subcontract ? 'Subcontract' : 'Self-performed'}
          </span>
          {!isImmutable && !isLegacy && (
            <div style={{ display: 'flex', gap: '2px' }}>
              <button 
                className="btn btn-ghost btn-sm"
                onClick={onEdit}
                data-testid={`edit-section-${section.id}`}
                style={{ padding: '4px' }}
              >
                <Edit2 size={14} />
              </button>
              <button 
                className="btn btn-ghost btn-sm"
                onClick={onDelete}
                style={{ color: 'var(--status-error)', padding: '4px' }}
                data-testid={`delete-section-${section.id}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Body: Compact rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)', fontSize: '0.875rem' }}>
        {section.is_subcontract ? (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: 'var(--text-muted)' }}>Subcontract cost</span>
            <span style={{ color: 'var(--text-primary)' }}>£{section.subcontract_cost?.toFixed(2)}</span>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: 'var(--text-muted)' }}>Labour</span>
            <span style={{ color: 'var(--text-primary)' }}>{section.labour_days} days × £{section.labour_day_rate}</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--text-muted)' }}>Materials</span>
          <span style={{ color: 'var(--text-primary)' }}>£{section.material_cost_total?.toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--text-muted)' }}>Margin</span>
          <span style={{ color: 'var(--text-primary)' }}>{section.margin_percentage}%</span>
        </div>
      </div>

      {/* Footer: Bold totals */}
      <div style={{ 
        marginTop: 'var(--space-md)', 
        paddingTop: 'var(--space-md)', 
        borderTop: '1px solid var(--glass-border)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-xs)',
        fontSize: '0.875rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
          <span style={{ color: 'var(--text-muted)' }}>Cost total</span>
          <span style={{ color: 'var(--text-primary)' }}>£{section.section_cost_total?.toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
          <span style={{ color: 'var(--text-muted)' }}>Revenue total</span>
          <span style={{ color: 'var(--text-primary)' }}>£{section.section_revenue_total?.toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
          <span style={{ color: 'var(--text-muted)' }}>Profit</span>
          <span style={{ color: 'var(--text-primary)' }}>£{section.section_profit?.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}

function SectionForm({ initialData, onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    trade_type: initialData?.trade_type || '',
    is_subcontract: initialData?.is_subcontract || false,
    labour_days: initialData?.labour_days || 0,
    labour_day_rate: initialData?.labour_day_rate || 0,
    subcontract_cost: initialData?.subcontract_cost || 0,
    material_cost_total: initialData?.material_cost_total || 0,
    margin_percentage: initialData?.margin_percentage || 20
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form 
      onSubmit={handleSubmit}
      style={{
        padding: 'var(--space-md)',
        background: 'var(--glass-bg)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--accent-primary)',
        marginBottom: 'var(--space-md)'
      }}
      data-testid="section-form"
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
        <div className="form-group">
          <label className="form-label">Trade Type</label>
          <input
            type="text"
            className="form-input"
            value={formData.trade_type}
            onChange={(e) => setFormData({ ...formData, trade_type: e.target.value })}
            placeholder="e.g., Electrical, Plumbing, Carpentry"
            required
            data-testid="trade-type-input"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Type</label>
          <select
            className="form-input"
            value={formData.is_subcontract ? 'subcontract' : 'direct'}
            onChange={(e) => setFormData({ ...formData, is_subcontract: e.target.value === 'subcontract' })}
            data-testid="type-select"
          >
            <option value="direct">Direct Labour</option>
            <option value="subcontract">Subcontract</option>
          </select>
        </div>
      </div>

      {formData.is_subcontract ? (
        <div className="form-group" style={{ marginBottom: 'var(--space-md)' }}>
          <label className="form-label">Subcontract Cost (£)</label>
          <input
            type="number"
            className="form-input"
            value={formData.subcontract_cost}
            onChange={(e) => setFormData({ ...formData, subcontract_cost: parseFloat(e.target.value) || 0 })}
            min="0"
            step="0.01"
            data-testid="subcontract-cost-input"
          />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
          <div className="form-group">
            <label className="form-label">Labour Days</label>
            <input
              type="number"
              className="form-input"
              value={formData.labour_days}
              onChange={(e) => setFormData({ ...formData, labour_days: parseFloat(e.target.value) || 0 })}
              min="0"
              step="0.5"
              data-testid="labour-days-input"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Day Rate (£)</label>
            <input
              type="number"
              className="form-input"
              value={formData.labour_day_rate}
              onChange={(e) => setFormData({ ...formData, labour_day_rate: parseFloat(e.target.value) || 0 })}
              min="0"
              step="1"
              data-testid="day-rate-input"
            />
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
        <div className="form-group">
          <label className="form-label">Material Cost (£)</label>
          <input
            type="number"
            className="form-input"
            value={formData.material_cost_total}
            onChange={(e) => setFormData({ ...formData, material_cost_total: parseFloat(e.target.value) || 0 })}
            min="0"
            step="0.01"
            data-testid="material-cost-input"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Margin (%)</label>
          <input
            type="number"
            className="form-input"
            value={formData.margin_percentage}
            onChange={(e) => setFormData({ ...formData, margin_percentage: parseFloat(e.target.value) || 0 })}
            min="0"
            max="100"
            step="0.1"
            required
            data-testid="margin-input"
          />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-sm)' }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={submitting}>
          <X size={14} /> Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={submitting} data-testid="save-section-btn">
          {submitting ? <Loader2 size={14} className="spin" /> : <Check size={14} />}
          {initialData ? 'Update' : 'Add'} Section
        </button>
      </div>
    </form>
  );
}

function SummaryItem({ label, value, highlight, success }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ 
        fontSize: '0.75rem', 
        color: 'var(--text-muted)', 
        marginBottom: '4px' 
      }}>
        {label}
      </div>
      <div style={{ 
        fontSize: '1rem', 
        fontWeight: 600,
        color: highlight ? 'var(--accent-primary)' : success ? 'var(--status-success)' : 'var(--text-primary)'
      }}>
        {value}
      </div>
    </div>
  );
}

export default QuoteSections;
